/**
 * Task Types for Finbot Chatbot
 */

export type TaskStatus = 'PENDING' | 'QUEUED' | 'RUNNING' | 'PAUSED' | 'COMPLETED' | 'FAILED' | 'CANCELLED' | 'RETRYING';
export type TaskPriority = 'CRITICAL' | 'HIGH' | 'NORMAL' | 'LOW' | 'BATCH';

export type TaskEventType =
  | 'SUBMITTED'
  | 'QUEUED'
  | 'STARTED'
  | 'PROGRESS'
  | 'COMPLETED'
  | 'FAILED'
  | 'RETRYING'
  | 'CANCELLED'
  | 'STALE_DETECTED'
  | 'REQUEUED';

export type TaskType = 'kg_import' | 'kg_build' | 'kg_update' | 'kg_delete' | 'document_ingest' | 'sync' | 'ifc_web_sync';

export interface TaskState {
  id: string;
  taskId: string;
  taskType: TaskType;
  userId: string;
  namespace?: string;
  priority: TaskPriority;
  status: TaskStatus;
  progressPercentage: number;
  progressMessage?: string;
  leaseOwner?: string;
  leaseExpiresAt?: string;
  retryCount: number;
  maxRetries: number;
  nextRetryAt?: string;
  inputData?: Record<string, unknown>;
  resultData?: Record<string, unknown>;
  errorData?: TaskError;
  version: number;
  scheduledAt?: string;
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
  durationMinutes?: number;
  isStale?: boolean;
}

export interface TaskProgress {
  taskId: string;
  taskType: TaskType;
  status: TaskStatus;
  progressPercentage: number;
  progressMessage?: string;
  itemsProcessed?: number;
  itemsTotal?: number;
  startedAt?: string;
  estimatedTimeRemaining?: number;
}

export interface TaskEvent {
  id: string;
  taskStateId: string;
  eventType: TaskEventType;
  eventPayload: Record<string, unknown>;
  createdAt: string;
}

export interface TaskError {
  code?: string;
  message: string;
  stack?: string;
  details?: Record<string, unknown>;
  recoverable?: boolean;
}

export interface DlqRecord {
  id: string;
  taskStateId?: string;
  originalQueue: string;
  originalMessage: Record<string, unknown>;
  failureReason: string;
  failureCount: number;
  firstFailureAt: string;
  lastFailureAt: string;
  resolved: boolean;
  resolvedAt?: string;
  resolvedBy?: string;
  resolutionNotes?: string;
  createdAt: string;
}

export interface TaskStatistics {
  pendingCount: number;
  queuedCount: number;
  runningCount: number;
  completedCount: number;
  failedCount: number;
  cancelledCount: number;
  retryingCount: number;
  avgDurationMinutes?: number;
  successRate?: number;
  unresolvedDlqCount: number;
  since?: string;
  computedAt: string;
}

export interface QueueStatistics {
  queueName: string;
  messageCount: number;
  consumerCount: number;
  publishRate?: number;
  consumeRate?: number;
  healthStatus: 'healthy' | 'degraded' | 'unhealthy';
}

export interface CreateTaskRequest {
  taskType: TaskType;
  payload: Record<string, unknown>;
  priority?: TaskPriority;
  namespace?: string;
  maxRetries?: number;
  scheduledAt?: string;
}

export interface CancelTaskRequest {
  taskId: string;
  reason?: string;
}

export interface TaskListFilters {
  status?: TaskStatus;
  taskType?: TaskType;
  namespace?: string;
  userId?: string;
  since?: Date;
  limit?: number;
  offset?: number;
}

export interface TaskStatusConfig {
  label: string;
  color: 'blue' | 'yellow' | 'green' | 'red' | 'gray' | 'orange';
  icon: 'clock' | 'queue' | 'play' | 'pause' | 'check' | 'x' | 'repeat';
  isTerminal: boolean;
}

export const TASK_STATUS_CONFIG: Record<TaskStatus, TaskStatusConfig> = {
  PENDING: { label: 'Chờ xử lý', color: 'gray', icon: 'clock', isTerminal: false },
  QUEUED: { label: 'Đang chờ', color: 'blue', icon: 'queue', isTerminal: false },
  RUNNING: { label: 'Đang chạy', color: 'blue', icon: 'play', isTerminal: false },
  PAUSED: { label: 'Tạm dừng', color: 'yellow', icon: 'pause', isTerminal: false },
  COMPLETED: { label: 'Hoàn thành', color: 'green', icon: 'check', isTerminal: true },
  FAILED: { label: 'Thất bại', color: 'red', icon: 'x', isTerminal: true },
  CANCELLED: { label: 'Đã hủy', color: 'gray', icon: 'x', isTerminal: true },
  RETRYING: { label: 'Thử lại', color: 'orange', icon: 'repeat', isTerminal: false }
};

export interface TaskPriorityConfig {
  label: string;
  color: 'red' | 'orange' | 'blue' | 'gray';
}

export const TASK_PRIORITY_CONFIG: Record<TaskPriority, TaskPriorityConfig> = {
  CRITICAL: { label: 'Khẩn cấp', color: 'red' },
  HIGH: { label: 'Cao', color: 'orange' },
  NORMAL: { label: 'Bình thường', color: 'blue' },
  LOW: { label: 'Thấp', color: 'gray' },
  BATCH: { label: 'Hàng loạt', color: 'gray' }
};

export const TASK_TYPE_LABELS: Record<TaskType, string> = {
  kg_import: 'Nhập Knowledge Graph',
  kg_build: 'Xây dựng Knowledge Graph',
  kg_update: 'Cập nhật Knowledge Graph',
  kg_delete: 'Xóa Knowledge Graph',
  document_ingest: 'Nhập tài liệu',
  sync: 'Đồng bộ dữ liệu',
  ifc_web_sync: 'Đồng bộ IFC Web'
};

/**
 * Check if a task status is terminal (completed, failed, or cancelled)
 */
export function isTaskTerminal(status: TaskStatus): boolean {
  return TASK_STATUS_CONFIG[status]?.isTerminal ?? false;
}
